// src/components/GestionLots.js
import React, { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ajouterLot, ecouterLots, supprimerLot,
  modifierLot, reordonnerLots,
} from "../services/db";
import { useTombola } from "../contexts/TombolaContext";
import { useAuth } from "../contexts/AuthContext";

const TOP3 = {
  1: { label: "1er prix", emoji: "🥇", color: "#b8860b", bg: "#fffbee", border: "#ffc107" },
  2: { label: "2e prix", emoji: "🥈", color: "#707070", bg: "#f8f8f8", border: "#bdbdbd" },
  3: { label: "3e prix", emoji: "🥉", color: "#a0522d", bg: "#fff5f0", border: "#e07040" },
};
function rs(rang) {
  return TOP3[rang] || { label: `${rang}e prix`, emoji: "🎁", color: "#555", bg: "#f7f8fc", border: "#ddd" };
}

// ─── Modal modification lot ───────────────────────────────────────────────────
function ModalModifLot({ lot, onSave, onClose }) {
  const [form, setForm] = useState({
    description: lot.description || "",
    valeur: lot.valeur || "",
    offertPar: lot.offertPar || "",
    rang: lot.rang || 1,
  });
  return (
    <div style={ms.overlay}>
      <div style={ms.modal}>
        <h3 style={ms.title}>✏️ Modifier le lot</h3>
        <div style={ms.field}>
          <label style={ms.label}>Description *</label>
          <input type="text" value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
            style={ms.input} required />
        </div>
        <div style={ms.row}>
          <div style={{flex:1}}>
            <label style={ms.label}>Valeur estimée</label>
            <input type="text" value={form.valeur}
              onChange={(e) => setForm({...form, valeur: e.target.value})}
              style={ms.input} placeholder="Ex : 45€" />
          </div>
          <div style={{flex:1}}>
            <label style={ms.label}>Rang</label>
            <input type="number" min="1" max="99" value={form.rang}
              onChange={(e) => setForm({...form, rang: Number(e.target.value)})}
              style={ms.input} />
          </div>
        </div>
        <div style={ms.field}>
          <label style={ms.label}>🤝 Offert par (optionnel)</label>
          <input type="text" value={form.offertPar}
            onChange={(e) => setForm({...form, offertPar: e.target.value})}
            style={ms.input} placeholder="Ex : Garage Dupont…" />
        </div>
        <div style={ms.actions}>
          <button onClick={onClose} style={ms.btnCancel}>Annuler</button>
          <button onClick={() => onSave(form)} disabled={!form.description.trim()} style={ms.btnSave}>
            💾 Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
const ms = {
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000},
  modal:{background:"#fff",borderRadius:"16px",padding:"1.75rem",maxWidth:"420px",width:"92%"},
  title:{margin:"0 0 1.25rem",fontSize:"1rem",fontWeight:"700",color:"#1a1a2e"},
  field:{marginBottom:"0.85rem"},
  row:{display:"flex",gap:"12px",marginBottom:"0.85rem"},
  label:{display:"block",fontSize:"0.8rem",fontWeight:"600",color:"#555",marginBottom:"4px"},
  input:{width:"100%",padding:"0.6rem 0.85rem",border:"1.5px solid #e0e0e0",borderRadius:"8px",fontSize:"0.92rem",outline:"none",boxSizing:"border-box"},
  actions:{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"1.25rem"},
  btnCancel:{padding:"0.6rem 1.25rem",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:"8px",cursor:"pointer",fontWeight:"600"},
  btnSave:{padding:"0.6rem 1.25rem",background:"#667eea",color:"#fff",border:"none",borderRadius:"8px",cursor:"pointer",fontWeight:"700"},
};

// ─── Composant principal ──────────────────────────────────────────────────────
export default function GestionLots() {
  const { activeTombolaId, activeTombola, isAdminTombola } = useTombola();
  const { orgId, isAdmin: isOrgAdmin } = useAuth();
  // Responsable = admin de l'association ou admin de cette tombola
  const isAdmin = isOrgAdmin || isAdminTombola;

  const [lots, setLots] = useState([]);
  const [form, setForm] = useState({ description:"", valeur:"", offertPar:"", rang:1 });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lotAModifier, setLotAModifier] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const isArchived = activeTombola?.archived;

  useEffect(() => {
    if (!orgId || !activeTombolaId) return;
    return ecouterLots(orgId, activeTombolaId, setLots);
  }, [orgId, activeTombolaId]);

  function showMsg(text, type = "success") {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  }

  async function handleAjouter(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await ajouterLot(orgId, activeTombolaId, form);
      showMsg(`✅ Lot "${form.description}" ajouté.`);
      setForm((f) => ({ ...f, description:"", valeur:"", offertPar:"" }));
    } catch { showMsg("Erreur.", "error"); }
    setLoading(false);
  }

  async function handleSupprimer(lot) {
    try {
      await supprimerLot(orgId, activeTombolaId, lot.id);
      showMsg(`🗑️ Lot supprimé.`);
    } catch { showMsg("Erreur.", "error"); }
    setConfirmDelete(null);
  }

  async function handleSaveModif(formData) {
    try {
      await modifierLot(orgId, activeTombolaId, lotAModifier.id, formData);
      showMsg("✅ Lot modifié.");
    } catch { showMsg("Erreur.", "error"); }
    setLotAModifier(null);
  }

  // ─── Drag & Drop ────────────────────────────────────────────────────────────
  function handleDragStart(e, index) {
    dragItem.current = index;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragEnter(e, index) {
    e.preventDefault();
    dragOver.current = index;
    setDragOverIndex(index);
  }

  function handleDragOver(e) { e.preventDefault(); }

  function handleDragEnd() { setDragIndex(null); setDragOverIndex(null); }

  // Flèches ↑ ↓ : fonctionnent au doigt sur téléphone (le glisser-déposer non)
  async function deplacer(index, delta) {
    const cible = index + delta;
    if (cible < 0 || cible >= lots.length) return;
    const newLots = [...lots];
    [newLots[index], newLots[cible]] = [newLots[cible], newLots[index]];
    setLots(newLots);
    try { await reordonnerLots(orgId, activeTombolaId, newLots); }
    catch { showMsg("Erreur de réordonnancement.", "error"); }
  }

  async function handleDrop(e) {
    e.preventDefault();
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      setDragIndex(null); setDragOverIndex(null); return;
    }
    const newLots = [...lots];
    const dragged = newLots.splice(dragItem.current, 1)[0];
    newLots.splice(dragOver.current, 0, dragged);
    dragItem.current = null; dragOver.current = null;
    setDragIndex(null); setDragOverIndex(null);
    setLots(newLots);
    try { await reordonnerLots(orgId, activeTombolaId, newLots); }
    catch { showMsg("Erreur de réordonnancement.", "error"); }
  }

  const previewRang = rs(Number(form.rang));
  const lotsUrl = `${window.location.origin}/lots/${orgId}/${activeTombolaId}`;
  const tirageEffectue = lots.some((l) => l.gagnantBilletId);

  return (
    <div>
      <div style={s.titleRow}>
        <h2 style={s.title}>🎁 Lots à gagner</h2>
        {isAdmin && activeTombolaId && (
          <div style={s.titleActions}>
            <button onClick={() => setShowQR(!showQR)} style={s.btnQR}>📱 QR Code public</button>
            <button onClick={() => window.print()} style={s.btnPrint}>🖨️ Affiche A4</button>
          </div>
        )}
      </div>

      {/* ── Affiche imprimable (A4 Strict Monopage) ── */}
{/* ── Affiche imprimable (A4 Strict - Modèle Souhaité) ── */}
      <div id="affiche-print" style={ap.wrapper}>
        
        {/* Logo centré en haut */}
        <div style={ap.header}>
          <img src="/logo_app.png" alt="TomboLive" style={ap.logo} />
        </div>

        {/* Nom de la tombola en grand et bleu */}
        <h1 style={ap.title}>{activeTombola?.nom || "NOM DE LA TOMBOLA"}</h1>

        {/* Zone centrale : QR Code + Texte */}
        <div style={ap.qrSection}>
          <div style={ap.qrWrapper}>
            {activeTombolaId && (
              <QRCodeSVG value={lotsUrl} size={260} level="H" fgColor="#1a3a8f" bgColor="#ffffff" />
            )}
          </div>
          <p style={ap.qrLabel}>Scannez pour voir les lots et suivre le tirage</p>
        </div>

        {/* Footer : Bandeau plein écran bleu uni tout en bas */}
        <div style={ap.footer}>
          Vous organisez une Tombola ? Simplifiez-vous la gestion sur tombo.live
        </div>
      </div>

      {/* Isolation totale et forçage de la mise en page d'impression */}
<style>{`
        /* 🚨 LE CORRECTIF : Le !important force l'affiche à rester cachée sur l'écran */
        #affiche-print { display: none !important; }

        @media print {
          html, body { 
            background: #fff !important; 
            margin: 0 !important; 
            padding: 0 !important; 
            overflow: hidden !important;
            height: 297mm !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * { visibility: hidden !important; }
          #affiche-print, #affiche-print * { visibility: visible !important; }
          #affiche-print {
            display: flex !important; /* Devient un flex uniquement à l'impression */
            position: absolute !important; 
            top: 0 !important; 
            left: 0 !important;
            width: 210mm !important; 
            height: 297mm !important;
            box-sizing: border-box !important;
            background: #ffffff !important;
            z-index: 9999999 !important;
          }
        }
        @page { size: A4 portrait; margin: 0; }
      `}</style>

      {isArchived && <div style={s.archivedBanner}>📦 Tombola archivée — consultation seule.</div>}
      {msg && <div style={{...s.msg,...(msg.type==="error"?s.msgErr:s.msgOk)}}>{msg.text}</div>}

      {lotAModifier && <ModalModifLot lot={lotAModifier} onSave={handleSaveModif} onClose={() => setLotAModifier(null)} />}

      {/* QR Code public */}
      {showQR && (
        <div style={s.qrBox}>
          <p style={s.qrTitle}>Page publique des lots</p>
          <QRCodeSVG value={lotsUrl} size={200} level="M" fgColor="#1a3a8f" />
          <button onClick={() => { navigator.clipboard.writeText(lotsUrl); }} style={s.qrCopyBtn}>
            📋 Copier le lien
          </button>
        </div>
      )}

      {/* Formulaire ajout */}
      {!isArchived && isAdmin && (
        <div style={s.formCard}>
          <h3 style={s.formTitle}>Ajouter un lot</h3>
          <form onSubmit={handleAjouter}>
            <div style={s.fieldFull}>
              <label style={s.label}>Description *</label>
              <input type="text" placeholder="Ex : Bouteille de Champagne"
                value={form.description} onChange={(e) => setForm({...form,description:e.target.value})}
                required style={s.input} />
            </div>
            <div style={s.rowTwo}>
              <div style={s.fieldHalf}>
                <label style={s.label}>Valeur estimée</label>
                <input type="text" placeholder="Ex : 45"
                  value={form.valeur} onChange={(e) => setForm({...form,valeur:e.target.value})}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && !v.includes("€")) setForm((f) => ({...f, valeur: v + "€"}));
                  }}
                  style={s.input} />
              </div>
              <div style={s.fieldHalf}>
                <label style={s.label}>Rang initial</label>
                <input type="number" min="1" max="99"
                  value={form.rang} onChange={(e) => setForm({...form,rang:Number(e.target.value)})}
                  required style={s.input} />
              </div>
            </div>
            <div style={s.fieldFull}>
              <label style={s.label}>🤝 Offert par (optionnel)</label>
              <input type="text" placeholder="Ex : Garage Dupont…"
                value={form.offertPar} onChange={(e) => setForm({...form,offertPar:e.target.value})}
                style={s.input} />
            </div>
            <div style={s.rangPreview}>
              <span style={{...s.rangBadge,background:previewRang.bg,color:previewRang.color,borderColor:previewRang.border}}>
                {previewRang.emoji} {previewRang.label}
              </span>
              <span style={s.rangHint}>Glissez-déposez pour réordonner.</span>
            </div>
            <button type="submit" disabled={loading} style={s.btnPrimary}>
              {loading ? "…" : "+ Ajouter le lot"}
            </button>
          </form>
        </div>
      )}

      {/* Stats */}
      <div style={s.statsRow}>
        <span style={s.badge}>{lots.length} lot(s)</span>
        {tirageEffectue && (
          <span style={{...s.badge,...s.badgeG,marginLeft:"8px"}}>
            {lots.filter((l)=>l.gagnantBilletId).length} attribué(s)
          </span>
        )}
        {isAdmin && !isArchived && lots.length > 1 && (
          <span style={s.dragHint}>↑ ↓ pour changer l'ordre</span>
        )}
      </div>

      {/* Liste lots */}
      {lots.length === 0 ? (
        <div style={s.empty}>Aucun lot enregistré.</div>
      ) : (
        <div style={s.lotsList}>
          {lots.map((lot, index) => {
            const r = rs(lot.rang);
            const isDragging = dragIndex === index;
            const isOver = dragOverIndex === index && dragIndex !== index;
            return (
              <div key={lot.id}
                draggable={isAdmin && !isArchived}
                onDragStart={(e) => isAdmin && handleDragStart(e, index)}
                onDragEnter={(e) => isAdmin && handleDragEnter(e, index)}
                onDragOver={handleDragOver}
                onDrop={isAdmin ? handleDrop : undefined}
                onDragEnd={handleDragEnd}
                style={{
                  ...s.lotCard,
                  ...(lot.rang <= 3 ? {borderColor:r.border,borderWidth:"2px"} : {}),
                  ...(lot.gagnantBilletId ? s.lotCardGagnant : {}),
                  ...(isDragging ? s.lotCardDragging : {}),
                  ...(isOver ? s.lotCardOver : {}),
                }}
              >
                {isOver && <div style={s.dropIndicator} />}
                <div style={s.lotHeader}>
                  {isAdmin && !isArchived && <span style={s.dragHandle}>⠿</span>}
                  <span style={{...s.rangBadge,background:r.bg,color:r.color,borderColor:r.border}}>
                    {r.emoji} {r.label}
                  </span>
                  <div style={s.lotInfo}>
                    <span style={s.lotDesc}>{lot.description}</span>
                    <div style={s.lotMeta}>
                      {lot.valeur && <span style={s.lotVal}>{lot.valeur}</span>}
                      {lot.offertPar && <span style={s.offertPar}>🤝 {lot.offertPar}</span>}
                    </div>
                  </div>
                  {isAdmin && !isArchived && (
                    <div style={s.lotActions}>
                      {!tirageEffectue && lots.length > 1 && (<>
                        <button onClick={() => deplacer(index, -1)} disabled={index === 0}
                          aria-label="Monter" style={{...s.btnEdit, ...(index === 0 ? s.btnOff : {})}}>↑</button>
                        <button onClick={() => deplacer(index, 1)} disabled={index === lots.length - 1}
                          aria-label="Descendre" style={{...s.btnEdit, ...(index === lots.length - 1 ? s.btnOff : {})}}>↓</button>
                      </>)}
                      <button onClick={() => setLotAModifier(lot)} style={s.btnEdit}>✏️</button>
                      <button onClick={() => setConfirmDelete(lot)} style={s.btnDel}>🗑️</button>
                    </div>
                  )}
                </div>
                {lot.gagnantBilletId && (
                  <div style={{...s.gagnantBanner,background:r.bg,borderColor:r.border}}>
                    🏆 <strong>{lot.gagnantNomPublic || lot.gagnantNom}</strong> — billet {lot.gagnantNumero}
                    <span style={s.contact}> · coordonnées dans l'onglet Tirage</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal suppression */}
      {confirmDelete && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <p style={s.modalText}>Supprimer <strong>"{confirmDelete.description}"</strong> ?</p>
            <div style={s.modalBtns}>
              <button onClick={() => setConfirmDelete(null)} style={s.btnCancel}>Annuler</button>
              <button onClick={() => handleSupprimer(confirmDelete)} style={s.btnDanger}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  titleRow:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1.25rem",flexWrap:"wrap",gap:"10px"},
  title:{fontSize:"1.25rem",fontWeight:"700",color:"#1a1a2e",margin:0},
  titleActions:{display:"flex",gap:"8px"},
  btnQR:{padding:"0.5rem 0.9rem",background:"#eef0ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.82rem"},
  btnPrint:{padding:"0.5rem 0.9rem",background:"linear-gradient(135deg,#1a3a8f,#0099cc)",color:"#fff",border:"none",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.82rem"},
  qrBox:{background:"#f8f9ff",borderRadius:"14px",padding:"1.5rem",textAlign:"center",marginBottom:"1.25rem",border:"2px solid #c7d2ff"},
  qrTitle:{fontWeight:"700",color:"#1a3a8f",fontSize:"0.95rem",margin:"0 0 1rem"},
  printPage:{ width:"210mm", height:"297mm", padding:"20mm 15mm 15mm 15mm", boxSizing:"border-box", overflow:"hidden", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"space-between", background:"#ffffff", color:"#1a1a2e", fontFamily:"'Segoe UI', system-ui, sans-serif" },
  printHeader:{ display:"flex", flexDirection:"column", alignItems:"center", width:"100%" },
  printLogo:{ maxWidth:"40mm", maxHeight:"40mm", objectFit:"contain", marginBottom:"8mm" },
  printTitle:{ fontSize:"2.5rem", fontWeight:"900", color:"#1a3a8f", textAlign:"center", margin:"0 0 4mm 0", lineHeight:"1.2", textTransform:"uppercase" },
  printDivider:{ width:"60px", height:"4px", background:"linear-gradient(90deg,#667eea,#764ba2)", borderRadius:"2px" },
  printLotsSection:{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", margin:"5mm 0" },
  printSectionTitle:{ fontSize:"1.1rem", fontWeight:"800", color:"#764ba2", letterSpacing:"1px", marginBottom:"6mm" },
  printLotsContainer:{ width:"100%", display:"flex", flexDirection:"column", gap:"5mm" },
  printLotRow:{ display:"flex", alignItems:"center", gap:"6mm", padding:"5mm 6mm", background:"#f8fafc", borderRadius:"14px", border:"1.5px solid #e2e8f0", boxSizing:"border-box" },
  printLotEmoji:{ fontSize:"2.5rem", lineHeight:"1" },
  printLotMeta:{ display:"flex", flexDirection:"column", gap:"1mm" },
  printLotDesc:{ fontSize:"1.4rem", fontWeight:"800", color:"#1a1a2e" },
  printLotVal:{ fontSize:"0.95rem", color:"#64748b", fontWeight:"600" },
  printQrSection:{ display:"flex", flexDirection:"column", alignItems:"center", gap:"5mm", margin:"5mm 0" },
  printQrWrapper:{ padding:"4mm", background:"#ffffff", borderRadius:"16px", border:"1px solid #e2e8f0", display:"inline-flex" },
  printQrText:{ fontSize:"1.3rem", fontWeight:"800", color:"#1a3a8f", textAlign:"center", margin:0 },
  printFooter:{ fontSize:"0.95rem", color:"#94a3b8", fontWeight:"700", textAlign:"center", borderTop:"1px dashed #e2e8f0", width:"100%", paddingTop:"6mm" },
  qrCopyBtn:{marginTop:"0.75rem",padding:"5px 14px",background:"#eef0ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontSize:"0.78rem",fontWeight:"600"},
  archivedBanner:{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:"8px",padding:"0.65rem 1rem",marginBottom:"1rem",color:"#e65100",fontSize:"0.88rem",fontWeight:"600"},
  msg:{borderRadius:"8px",padding:"0.75rem 1rem",marginBottom:"1rem",fontSize:"0.9rem"},
  msgOk:{background:"#e8f5e9",color:"#1b5e20"},
  msgErr:{background:"#ffebee",color:"#b71c1c"},
  formCard:{background:"#f7f8fc",borderRadius:"12px",padding:"1.25rem",marginBottom:"1.25rem",border:"1px solid #eee"},
  formTitle:{margin:"0 0 1rem",fontSize:"0.95rem",fontWeight:"700",color:"#444"},
  fieldFull:{marginBottom:"0.85rem"},
  rowTwo:{display:"flex",gap:"12px",marginBottom:"0.85rem"},
  fieldHalf:{flex:1,minWidth:0},
  label:{display:"block",fontSize:"0.8rem",fontWeight:"600",color:"#555",marginBottom:"4px"},
  input:{width:"100%",padding:"0.55rem 0.8rem",border:"1.5px solid #e0e0e0",borderRadius:"8px",fontSize:"0.92rem",outline:"none",boxSizing:"border-box",background:"#fff"},
  rangPreview:{display:"flex",alignItems:"center",gap:"10px",marginBottom:"0.85rem",flexWrap:"wrap"},
  rangBadge:{padding:"4px 12px",borderRadius:"20px",fontSize:"0.82rem",fontWeight:"700",border:"1px solid",display:"inline-flex",alignItems:"center",gap:"4px",flexShrink:0},
  rangHint:{fontSize:"0.78rem",color:"#888"},
  btnPrimary:{padding:"0.65rem 1.5rem",background:"linear-gradient(135deg,#764ba2,#667eea)",color:"#fff",border:"none",borderRadius:"8px",fontWeight:"700",cursor:"pointer",width:"100%"},
  statsRow:{display:"flex",alignItems:"center",gap:"8px",marginBottom:"1.25rem",flexWrap:"wrap"},
  badge:{background:"#f3e8ff",color:"#6b21a8",borderRadius:"20px",padding:"4px 12px",fontSize:"0.82rem",fontWeight:"600"},
  badgeG:{background:"#fff3cd",color:"#856404"},
  dragHint:{fontSize:"0.78rem",color:"#aaa",marginLeft:"auto"},
  empty:{textAlign:"center",color:"#aaa",padding:"2rem",fontStyle:"italic"},
  lotsList:{display:"flex",flexDirection:"column",gap:"8px"},
  lotCard:{background:"#fff",border:"1.5px solid #eee",borderRadius:"12px",padding:"0.9rem 1rem",position:"relative",transition:"all 0.15s"},
  lotCardGagnant:{background:"#fffdf0"},
  lotCardDragging:{opacity:0.4,transform:"scale(0.98)",border:"2px dashed #667eea"},
  lotCardOver:{boxShadow:"0 -3px 0 0 #667eea",borderColor:"#667eea"},
  dropIndicator:{position:"absolute",top:"-3px",left:"10px",right:"10px",height:"3px",background:"#667eea",borderRadius:"2px"},
  lotHeader:{display:"flex",alignItems:"center",gap:"8px"},
  dragHandle:{fontSize:"1.2rem",color:"#bbb",cursor:"grab",flexShrink:0,userSelect:"none",padding:"0 4px"},
  lotInfo:{flex:1,minWidth:0},
  lotDesc:{display:"block",fontWeight:"600",color:"#1a1a2e",fontSize:"0.93rem"},
  lotMeta:{display:"flex",alignItems:"center",gap:"6px",flexWrap:"wrap",marginTop:"3px"},
  lotVal:{background:"#f0f0f0",color:"#555",fontSize:"0.75rem",padding:"2px 8px",borderRadius:"4px"},
  offertPar:{background:"#e8f5e9",color:"#2e7d32",fontSize:"0.75rem",padding:"2px 8px",borderRadius:"4px",fontWeight:"600"},
  lotActions:{display:"flex",gap:"4px",flexShrink:0},
  btnEdit:{background:"none",border:"1px solid #e0e0e0",borderRadius:"6px",padding:"3px 7px",cursor:"pointer",fontSize:"0.82rem"},
  btnDel:{background:"none",border:"1px solid #ffcdd2",borderRadius:"6px",padding:"3px 7px",cursor:"pointer",fontSize:"0.82rem"},
  btnOff:{opacity:0.3,cursor:"default"},
  gagnantBanner:{marginTop:"10px",padding:"8px 12px",borderRadius:"6px",border:"1px solid",fontSize:"0.85rem",color:"#555"},
  contact:{color:"#888"},
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000},
  modal:{background:"#fff",borderRadius:"14px",padding:"1.75rem",maxWidth:"360px",width:"90%"},
  modalText:{fontSize:"0.95rem",color:"#333",marginBottom:"1.25rem",lineHeight:1.6},
  modalBtns:{display:"flex",gap:"10px",justifyContent:"flex-end"},
  btnCancel:{padding:"0.6rem 1.25rem",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:"8px",cursor:"pointer",fontWeight:"600"},
  btnDanger:{padding:"0.6rem 1.25rem",background:"#e53935",color:"#fff",border:"none",borderRadius:"8px",cursor:"pointer",fontWeight:"600"},
};

const ap = {
  wrapper: {
    width: "210mm",
    height: "297mm",
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between", // Aligne le footer tout en bas et distribue le reste
    background: "#ffffff",
    paddingTop: "25mm", // Marge avant le logo
    color: "#1a3a8f",
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  logo: {
    width: "55mm", // Taille parfaite pour correspondre à ton image
    height: "auto",
    objectFit: "contain",
  },
  title: {
    fontSize: "2.8rem",
    fontWeight: "800",
    color: "#1a3a8f",
    textAlign: "center",
    margin: "10mm 10mm 0 10mm",
    lineHeight: "1.3",
    textTransform: "uppercase",
    letterSpacing: "-0.5px",
  },
  qrSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8mm",
    margin: "10mm 0",
  },
  qrWrapper: {
    padding: "5mm",
    background: "#ffffff",
    borderRadius: "4px",
    display: "inline-flex",
  },
  qrLabel: {
    fontSize: "1.2rem",
    fontWeight: "700",
    color: "#1a3a8f",
    textAlign: "center",
    margin: 0,
  },
  footer: {
    width: "100%",
    background: "#1a3a8f", // Le bleu uni de ta charte
    color: "#ffffff",
    fontSize: "1rem",
    fontWeight: "600",
    textAlign: "center",
    padding: "6mm 0", // Hauteur du bandeau du bas
    marginTop: "auto",
    boxSizing: "border-box",
  },
};